import { useGetTrainingCourseFilters } from '@/hooks/useGetTrainingCourseFilters';
import { useLocale } from '@/providers/LocaleProvider';
import { KLAYO_COLORS } from '@/themes';
import { BaseSelectMenuOption, BaseSelectorOption } from '@/types/components/selector.types';
import { trainingCourseFilterSchema } from '@/utilities/trainingCourseFilterSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  Box,
  Checkbox,
  Collapse,
  Drawer,
  FormControlLabel,
  Slider,
  Stack,
  Typography,
} from '@mui/material';
import { Switch } from 'klayowebshared';
import { isEmpty } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Button } from './Button';
import { TextField } from './TextField';

export interface TrainingCourseFilters {
  providers: BaseSelectMenuOption[];
  regions: BaseSelectorOption[];
  faculties: BaseSelectorOption[];
  courseTypes: BaseSelectorOption[];
  languages: BaseSelectorOption[];
  priceMin: number | null;
  priceMax: number | null;
  hasEnrolments: boolean;
}

interface TrainingCourseFilterDrawerProps {
  open: boolean;
  onClose: () => void;
  onApply: (filters: TrainingCourseFilters) => void;
  filters: TrainingCourseFilters;
  providers: [string, BaseSelectMenuOption][];
}

const COLLAPSED_COUNT = 5;

const getOptionKey = (option: BaseSelectorOption) => option.id ?? option.value ?? option.name ?? '';

const isSelected = (option: BaseSelectorOption, selected: BaseSelectorOption[]) =>
  selected.some((s) => getOptionKey(s) === getOptionKey(option));

const toggleOption = (option: BaseSelectorOption, selected: BaseSelectorOption[]) =>
  isSelected(option, selected)
    ? selected.filter((s) => getOptionKey(s) !== getOptionKey(option))
    : [...selected, option];

interface CheckboxGroupProps {
  label: string;
  options: BaseSelectorOption[];
  selected: BaseSelectorOption[];
  onChange: (next: BaseSelectorOption[]) => void;
  singleSelect?: boolean;
  noCollapse?: boolean;
  /** Controlled "Show all" state, owned by the drawer so it survives the drawer unmounting on
   *  close (MUI Drawer doesn't keepMounted). */
  expanded?: boolean;
  onToggleExpanded?: () => void;
}

const CheckboxGroup = ({
  label,
  options,
  selected,
  onChange,
  singleSelect,
  noCollapse,
  expanded = false,
  onToggleExpanded,
}: CheckboxGroupProps) => {
  const { translate } = useLocale();

  const alwaysVisible = noCollapse ? options : options.slice(0, COLLAPSED_COUNT);
  const collapsible = noCollapse ? [] : options.slice(COLLAPSED_COUNT);
  const hasMore = collapsible.length > 0;

  if (isEmpty(options)) return null;

  const renderOption = (option: BaseSelectorOption) => (
    <FormControlLabel
      key={String(getOptionKey(option))}
      control={
        <Checkbox
          checked={isSelected(option, selected)}
          onChange={() =>
            onChange(
              singleSelect
                ? isSelected(option, selected)
                  ? []
                  : [option]
                : toggleOption(option, selected)
            )
          }
          size='medium'
          sx={{
            color: KLAYO_COLORS.GreyGR500,
            '&.Mui-checked': { color: KLAYO_COLORS.NeonBlueNB500 },
            p: '4px',
            mr: '4px',
          }}
        />
      }
      label={
        <Typography variant='body1' color={KLAYO_COLORS.GreyGR900}>
          {option.label ?? option.name ?? ''}
        </Typography>
      }
      sx={{ ml: 0, mr: 0 }}
    />
  );

  return (
    <Stack spacing={0.5}>
      <Typography
        variant='body1'
        fontSize={18}
        fontWeight={500}
        color={KLAYO_COLORS.GreyGR900}
        mb={1.5}
      >
        {label}
      </Typography>
      {alwaysVisible.map(renderOption)}
      <Collapse in={expanded} timeout={300}>
        <Stack spacing={0.5}>{collapsible.map(renderOption)}</Stack>
      </Collapse>
      {hasMore && (
        <Stack
          direction='row'
          alignItems='center'
          spacing={0.5}
          onClick={onToggleExpanded}
          sx={{ cursor: 'pointer', width: 'fit-content', mt: 0.5 }}
        >
          <Typography variant='body1' color={KLAYO_COLORS.GreyGR900}>
            {expanded ? translate('common.showLess') : translate('common.showAll')}
          </Typography>
          <KeyboardArrowDownIcon
            sx={{
              fontSize: 20,
              color: KLAYO_COLORS.GreyGR900,
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 300ms ease',
            }}
          />
        </Stack>
      )}
    </Stack>
  );
};

export const TrainingCourseFilterDrawer = ({
  open,
  onClose,
  onApply,
  filters,
  providers,
}: TrainingCourseFilterDrawerProps) => {
  const { translate } = useLocale();
  const [localFilters, setLocalFilters] = useState<TrainingCourseFilters>(filters);
  // "Show all" expansion per group, kept here (not inside CheckboxGroup) so it survives the drawer
  // content unmounting when closed — reopening the filter keeps the groups the user expanded.
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const toggleGroup = (key: string) =>
    setExpandedGroups((prev) => ({ ...prev, [key]: !prev[key] }));

  const { data: filtersData } = useGetTrainingCourseFilters({
    providerIds: localFilters.providers.map((p) => p.value as string).filter(Boolean),
  });

  const regionOptions = useMemo<BaseSelectorOption[]>(
    () =>
      (filtersData?.filters?.regions ?? []).map((region) => ({
        id: region,
        label: region,
        name: region,
      })),
    [filtersData]
  );
  const facultyOptions = useMemo<BaseSelectorOption[]>(
    () =>
      (filtersData?.filters?.faculties ?? []).map((f) => ({
        id: f.id,
        label: f.name,
        name: f.name,
      })),
    [filtersData]
  );
  const courseTypeOptions = useMemo<BaseSelectorOption[]>(
    () =>
      (filtersData?.filters?.courseTypes ?? []).map((item) => ({
        id: item,
        label: item,
        name: item,
      })),
    [filtersData]
  );
  const languageOptions = useMemo<BaseSelectorOption[]>(
    () =>
      (filtersData?.filters?.languages ?? []).map((item) => {
        const displayName = translate(`languages.${item}`, { defaultValue: item });
        return {
          id: item,
          label: displayName,
          name: displayName,
        };
      }),
    [filtersData, translate]
  );
  const priceRange = useMemo(
    () =>
      filtersData?.filters
        ? {
            min: filtersData.filters.minPriceInCents / 100,
            max: filtersData.filters.maxPriceInCents / 100,
          }
        : undefined,
    [filtersData]
  );

  const schema = useMemo(() => trainingCourseFilterSchema(priceRange), [priceRange]);

  const {
    control,
    formState: { errors },
    reset,
    trigger,
    setValue,
    watch,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      priceMin: filters.priceMin,
      priceMax: filters.priceMax,
    },
    mode: 'onChange',
  });

  const priceMinValue = watch('priceMin');
  const priceMaxValue = watch('priceMax');

  useEffect(() => {
    setLocalFilters((prev) => ({
      ...prev,
      priceMin: priceMinValue,
      priceMax: priceMaxValue,
    }));
  }, [priceMinValue, priceMaxValue]);

  const hasErrors = !!errors.priceMin || !!errors.priceMax;

  const providerOptions = useMemo<BaseSelectorOption[]>(
    () =>
      providers.map(([, opt]) => ({
        id: String(opt.value),
        label: opt.label,
        name: opt.label,
        value: opt.value,
      })),
    [providers]
  );

  const selectedProviderOptions = useMemo<BaseSelectorOption[]>(
    () =>
      localFilters.providers.map((p) => ({
        id: String(p.value),
        label: p.label,
        name: p.label,
        value: p.value,
      })),
    [localFilters.providers]
  );

  // When the selected providers change, the available filter options are refetched (their union
  // across the selected providers). Drop any selected values that are no longer offered so we
  // never keep or send stale filters. Guarded on loaded data so it doesn't wipe valid selections
  // while options are still loading (e.g. right after the drawer opens with saved filters).
  useEffect(() => {
    if (!filtersData) return;
    const prune = (selected: BaseSelectorOption[], options: BaseSelectorOption[]) =>
      selected.filter((s) => options.some((o) => getOptionKey(o) === getOptionKey(s)));
    setLocalFilters((prev) => ({
      ...prev,
      regions: prune(prev.regions, regionOptions),
      faculties: prune(prev.faculties, facultyOptions),
      courseTypes: prune(prev.courseTypes, courseTypeOptions),
      languages: prune(prev.languages, languageOptions),
    }));
  }, [filtersData, regionOptions, facultyOptions, courseTypeOptions, languageOptions]);

  const handleOpen = () => {
    setLocalFilters(filters);
    reset({ priceMin: filters.priceMin, priceMax: filters.priceMax });
  };

  const handleApply = () => {
    if (hasErrors) return;
    onApply(localFilters);
    onClose();
  };

  const handleCancel = () => {
    setLocalFilters(filters);
    reset({ priceMin: filters.priceMin, priceMax: filters.priceMax });
    onClose();
  };

  return (
    <Drawer
      open={open}
      anchor='right'
      onClose={handleCancel}
      SlideProps={{ onEnter: handleOpen }}
      PaperProps={{
        sx: {
          width: { xs: '100vw', md: 480 },
          maxWidth: '100vw',
        },
      }}
    >
      <Stack direction='column' sx={{ height: '100%' }}>
        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 2.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${KLAYO_COLORS.GreyGR200}`,
          }}
        >
          <Typography variant='h6' fontWeight={600}>
            {translate('manage.filters')}
          </Typography>
          <Box
            component='span'
            onClick={handleCancel}
            sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
          >
            <CloseIcon sx={{ fontSize: 20, color: KLAYO_COLORS.GreyGR500 }} />
          </Box>
        </Box>

        {/* Content */}
        <Stack spacing={3} sx={{ px: 3, py: 3, flexGrow: 1, overflow: 'auto' }}>
          <CheckboxGroup
            label={translate('manage.provider')}
            options={providerOptions}
            selected={selectedProviderOptions}
            noCollapse
            onChange={(next) => {
              // Reset the price range (it's re-scoped per provider set); dependent list filters are
              // kept and pruned to the new option union by the effect above.
              setLocalFilters((prev) => ({
                ...prev,
                providers: next.map(
                  (p) => ({ value: p.value, label: p.label }) as BaseSelectMenuOption
                ),
                priceMin: null,
                priceMax: null,
              }));
              reset({ priceMin: null, priceMax: null });
            }}
          />

          <CheckboxGroup
            label={translate('manage.region')}
            options={regionOptions}
            selected={localFilters.regions}
            expanded={!!expandedGroups.regions}
            onToggleExpanded={() => toggleGroup('regions')}
            onChange={(next) => setLocalFilters((prev) => ({ ...prev, regions: next }))}
          />

          <CheckboxGroup
            label={translate('manage.faculty')}
            options={facultyOptions}
            selected={localFilters.faculties}
            expanded={!!expandedGroups.faculties}
            onToggleExpanded={() => toggleGroup('faculties')}
            onChange={(next) => setLocalFilters((prev) => ({ ...prev, faculties: next }))}
          />

          <CheckboxGroup
            label={translate('manage.courseType')}
            options={courseTypeOptions}
            selected={localFilters.courseTypes}
            expanded={!!expandedGroups.courseTypes}
            onToggleExpanded={() => toggleGroup('courseTypes')}
            onChange={(next) => setLocalFilters((prev) => ({ ...prev, courseTypes: next }))}
          />

          <CheckboxGroup
            label={translate('manage.language')}
            options={languageOptions}
            selected={localFilters.languages}
            expanded={!!expandedGroups.languages}
            onToggleExpanded={() => toggleGroup('languages')}
            onChange={(next) => setLocalFilters((prev) => ({ ...prev, languages: next }))}
          />

          {localFilters.providers.length > 0 && priceRange && (
            <Stack spacing={1}>
              <Typography
                variant='body1'
                fontSize={18}
                fontWeight={500}
                color={KLAYO_COLORS.GreyGR900}
              >
                {translate('manage.price')}
              </Typography>
              <Slider
                value={[
                  localFilters.priceMin ?? priceRange.min,
                  localFilters.priceMax ?? priceRange.max,
                ]}
                onChange={(_e, value) => {
                  const [min, max] = value as number[];
                  setValue('priceMin', min, { shouldValidate: true });
                  setValue('priceMax', max, { shouldValidate: true });
                }}
                min={priceRange.min}
                max={priceRange.max}
                step={1}
                valueLabelDisplay='off'
                sx={{
                  mx: 1,
                  '& .MuiSlider-thumb': {
                    backgroundColor: '#fff',
                    border: `2px solid ${KLAYO_COLORS.NeonBlueNB500}`,
                    width: 20,
                    height: 20,
                  },
                  '& .MuiSlider-track': { backgroundColor: KLAYO_COLORS.NeonBlueNB500 },
                  '& .MuiSlider-rail': { backgroundColor: KLAYO_COLORS.GreyGR200 },
                }}
              />
              <Stack direction='row' alignItems='center' gap={2}>
                <Controller
                  control={control}
                  name='priceMin'
                  render={({ field, fieldState }) => (
                    <TextField
                      type='number'
                      dense
                      value={field.value}
                      onChange={(e) => {
                        field.onChange(e.target.value === '' ? null : Number(e.target.value));
                        trigger('priceMax');
                      }}
                      error={!!fieldState.error}
                      leadingIcon={
                        <Typography variant='body2' color={KLAYO_COLORS.GreyGR500}>
                          $
                        </Typography>
                      }
                      sx={{ flex: 1 }}
                    />
                  )}
                />
                <Typography variant='body2' color={KLAYO_COLORS.GreyGR500}>
                  {translate('manage.to')}
                </Typography>
                <Controller
                  control={control}
                  name='priceMax'
                  render={({ field, fieldState }) => (
                    <TextField
                      type='number'
                      dense
                      value={field.value}
                      onChange={(e) => {
                        field.onChange(e.target.value === '' ? null : Number(e.target.value));
                        trigger('priceMin');
                      }}
                      error={!!fieldState.error}
                      leadingIcon={
                        <Typography variant='body2' color={KLAYO_COLORS.GreyGR500}>
                          $
                        </Typography>
                      }
                      sx={{ flex: 1 }}
                    />
                  )}
                />
              </Stack>
              {hasErrors && (
                <Typography variant='caption' color='error'>
                  {translate('manage.priceRangeError', {
                    min: priceRange.min,
                    max: priceRange.max,
                  })}
                </Typography>
              )}
            </Stack>
          )}

          {/* Enrolments toggle */}
          <Stack spacing={1}>
            <Typography
              variant='body1'
              fontSize={18}
              fontWeight={500}
              color={KLAYO_COLORS.GreyGR900}
            >
              {translate('manage.enrolments')}
            </Typography>
            <Switch
              label={translate('manage.onlyShowCoursesWithEnrolments')}
              checked={localFilters.hasEnrolments}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setLocalFilters((prev) => ({ ...prev, hasEnrolments: e.target.checked }))
              }
            />
          </Stack>
        </Stack>

        {/* Footer */}
        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: `1px solid ${KLAYO_COLORS.GreyGR200}`,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2,
          }}
        >
          <Button
            variant='outlined'
            label={translate('common.cancel')}
            size='md'
            onClick={handleCancel}
          />
          <Button
            variant='filled'
            label={translate('common.apply')}
            size='md'
            onClick={handleApply}
            disabled={hasErrors}
          />
        </Box>
      </Stack>
    </Drawer>
  );
};
